import { useEffect, useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/usePermissions";
import { Shield } from "lucide-react";

interface FragmentRowsProps {
  category: string;
  list: { key: string; label: string; description: string | null }[];
  matrix: Map<string, Set<string>>;
  toggle: (role: AppRole, perm: string, enable: boolean) => void;
}

const FragmentRows = ({ category, list, matrix, toggle }: FragmentRowsProps) => (
  <>
    <tr>
      <td colSpan={ROLES.length + 1} className="pt-6 pb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {category}
      </td>
    </tr>
    {list.map((p) => (
      <tr key={p.key} className="border-b border-border/50">
        <td className="py-3 pr-4">
          <div className="font-medium">{p.label}</div>
          {p.description && <div className="text-xs text-muted-foreground mt-0.5">{p.description}</div>}
        </td>
        {ROLES.map((r) => {
          const checked = r.key === "super_admin" || matrix.get(r.key)?.has(p.key) || false;
          return (
            <td key={r.key} className="text-center py-3 px-2">
              <Switch
                checked={checked}
                disabled={r.key === "super_admin"}
                onCheckedChange={(v) => toggle(r.key, p.key, v)}
              />
            </td>
          );
        })}
      </tr>
    ))}
  </>
);

type AppRole = "super_admin" | "admin" | "editor" | "viewer" | "support_manager";

const ROLES: { key: AppRole; label: string; description: string }[] = [
  { key: "super_admin", label: "Super Admin", description: "Full system access. Cannot be modified." },
  { key: "admin", label: "Administrator", description: "Manage content, users and analytics." },
  { key: "editor", label: "Editor", description: "Create and edit content." },
  { key: "viewer", label: "Viewer", description: "Read-only dashboard access." },
  { key: "support_manager", label: "Support Manager", description: "Handle tickets and user support." },
];

interface Permission {
  key: string;
  label: string;
  category: string;
  description: string | null;
}

const RolesPermissions = () => {
  const { toast } = useToast();
  const { isSuperAdmin, loading: permLoading } = usePermissions();
  const [perms, setPerms] = useState<Permission[]>([]);
  const [matrix, setMatrix] = useState<Map<string, Set<string>>>(new Map());
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data: catalog } = await supabase
      .from("permissions_catalog" as any)
      .select("*")
      .order("category");
    setPerms((catalog ?? []) as any);

    const { data: rolePerms } = await supabase
      .from("role_permissions" as any)
      .select("role, permission_key");
    const m = new Map<string, Set<string>>();
    (rolePerms ?? []).forEach((rp: any) => {
      if (!m.has(rp.role)) m.set(rp.role, new Set());
      m.get(rp.role)!.add(rp.permission_key);
    });
    setMatrix(m);
    setLoading(false);
  };

  useEffect(() => {
    if (!permLoading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permLoading]);

  const toggle = async (role: AppRole, permKey: string, enable: boolean) => {
    if (role === "super_admin") return;
    if (enable) {
      const { error } = await supabase
        .from("role_permissions" as any)
        .insert({ role, permission_key: permKey });
      if (error) {
        toast({ variant: "destructive", title: "Failed", description: error.message });
        return;
      }
    } else {
      const { error } = await supabase
        .from("role_permissions" as any)
        .delete()
        .eq("role", role)
        .eq("permission_key", permKey);
      if (error) {
        toast({ variant: "destructive", title: "Failed", description: error.message });
        return;
      }
    }
    // Update local state optimistically
    const newMatrix = new Map(matrix);
    if (!newMatrix.has(role)) newMatrix.set(role, new Set());
    if (enable) newMatrix.get(role)!.add(permKey);
    else newMatrix.get(role)!.delete(permKey);
    setMatrix(newMatrix);
    toast({ title: enable ? "Permission granted" : "Permission revoked" });
  };

  if (permLoading) return <AdminLayout><div className="p-8">Loading...</div></AdminLayout>;
  if (!isSuperAdmin) {
    return (
      <AdminLayout>
        <Card className="max-w-2xl mx-auto mt-12">
          <CardHeader><CardTitle>Restricted</CardTitle></CardHeader>
          <CardContent>
            <p className="text-muted-foreground">Only Super Admins can manage roles and permissions.</p>
          </CardContent>
        </Card>
      </AdminLayout>
    );
  }

  // Group permissions by category
  const grouped = perms.reduce((acc, p) => {
    if (!acc[p.category]) acc[p.category] = [];
    acc[p.category].push(p);
    return acc;
  }, {} as Record<string, Permission[]>);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Shield className="h-7 w-7" /> Roles & Permissions
          </h1>
          <p className="text-muted-foreground mt-1">
            Toggle granular permissions for each role. Changes take effect immediately.
          </p>
        </div>

        {loading ? (
          <Card><CardContent className="py-12 text-center">Loading...</CardContent></Card>
        ) : (
          <Card>
            <CardContent className="pt-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 pr-4 font-medium">Permission</th>
                      {ROLES.map((r) => (
                        <th key={r.key} className="text-center py-3 px-2 font-medium min-w-[110px]">
                          <div>{r.label}</div>
                          {r.key === "super_admin" && (
                            <Badge variant="outline" className="text-[10px] mt-1">all</Badge>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(grouped).map(([category, list]) => (
                      <FragmentRows key={category} category={category} list={list} matrix={matrix} toggle={toggle} />
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader><CardTitle>Role Descriptions</CardTitle></CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2">
              {ROLES.map((r) => (
                <div key={r.key} className="p-3 rounded-lg border border-border">
                  <div className="font-medium">{r.label}</div>
                  <div className="text-sm text-muted-foreground mt-1">{r.description}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default RolesPermissions;
